import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Button } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { getFeaturesAccess } from '../../../containers/actions/common/accessControl';
import { useHistory } from 'react-router-dom';
import {
  useScreenAccess,
  useScreensAccessLoaded,
  PD_SCREEN_VARIABLE,
} from './utils/useFeaturesAccess';
import CollapseProjectDetails from './CollapseProjectDetails';
import DocumentNavigationTabs from './DocumentNavigationTabs';
import { useStyles } from './styles/useStyles';
import { useProductDevelopment } from './hooks/useProductDevelopment';
import { useHandlers } from './handlers/useHandlers';
import { useProductDevelopmentHelpers } from './hooks/useProductDevelopmentHelpers';
import FilterSection from './components/FilterSection';
import ActionButtons from './components/ActionButtons';
import QuickSummarySection from './components/QuickSummarySection';
import SearchSection from './components/SearchSection';
import TableSection from './components/TableSection';
import RefreshSection from './components/RefreshSection';
import PaginationSection from './components/PaginationSection';
import ModalsSection from './components/ModalsSection';
import SnackbarSection from './components/SnackbarSection';
import FilterDialogSection from './components/FilterDialogSection';
import BackDropLoading from '../models/BackDrop';
import ProjectAnalytics from './ProjectAnalytics';
import BatchGenealogy from './BatchGenealogy';
import GanttChart from './GanttChart';
import ExecutiveSummary from './ExecutiveSummary';
import { ProjectDevNavContext } from './ProjectDevNavContext';

// Scroll region for the expanded (selected-project) view. It claims the space
// left under the "Return to Main Dashboard" button and becomes its own vertical
// scroll container so the filters / search / project row can scroll away while
// the tab strip (made sticky in ProjectTabs) stays pinned to the top. Rendered
// as a plain fragment on the dashboard list view so that page behaves unchanged.
const PdScrollWrapper = ({ selected, scrollRef, maxHeight, children }) => {
  if (!selected) return <>{children}</>;
  return (
    <div
      ref={scrollRef}
      className="pd-expanded-scroll"
      style={{
        maxHeight: maxHeight || undefined,
        overflowY: 'auto',
        // `auto` (not `hidden`) so a table sitting flush against the right edge
        // keeps its border instead of having it clipped away. The tab table has
        // its own contained scroll, so nothing wide overflows here anyway, and
        // the scrollbar itself is hidden via the `pd-expanded-scroll` class.
        overflowX: 'auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  );
};

const ProductDevelopment = () => {
  const classes = useStyles();

  // Product Development dashboard feature access. Controls which actions/buttons
  // this role may see; child components read the same redux slice with
  // useSelector (no prop-drilling).
  //
  // The screen id and storage key both come from /getScreensAccess rather than
  // being written here — same approach as the PO dashboards. Only fetch features
  // once the screen itself is granted (screenAccess === true).
  const dispatch = useDispatch();
  const screenAccess = useScreenAccess();
  const featuresAccess = useSelector(
    (state) =>
      state?.pcmp?.common?.accessControl?.featuresAccess?.[PD_SCREEN_VARIABLE]
  );
  React.useEffect(() => {
    if (screenAccess?.screenAccess && !featuresAccess) {
      dispatch(getFeaturesAccess(screenAccess.screenId, screenAccess.screenVariable));
    }
  }, [dispatch, screenAccess, featuresAccess]);

  // Direct-URL guard. Hiding the sidebar link doesn't protect the route — this
  // page is still reachable by typing the URL — so once /getScreensAccess has
  // resolved, anyone without Product Development access is sent to the PO
  // dashboard. `replace` (not `push`) so the browser Back button doesn't bounce
  // them straight back here.
  const history = useHistory();
  const screensAccessLoaded = useScreensAccessLoaded();
  const pdGranted = screenAccess?.screenAccess === true;
  React.useEffect(() => {
    // Undefined access while the call is in flight is NOT "denied" — wait for it.
    if (!screensAccessLoaded || pdGranted) return;
    history.replace(
      `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/purchaseorderManagement`
    );
  }, [screensAccessLoaded, pdGranted, history]);

  const {
    // State
    cmoSite,
    setCmoSite,
    cmo,
    setCmo,
    site,
    setSite,
    cmoOrgId,
    setCmoOrgId,
    initialCmoOrgId,
    deliverableType,
    setDeliverableType,
    searchCriteria,
    setSearchCriteria,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    initialCmoSite,
    initialDeliverableType,
    rowsPerPage,
    setRowsPerPage,
    filtersLoading,
    snackbar,
    setSnackbar,
    filterDialog,
    setFilterDialog,
    projects,
    totalProjects,
    quickSummaryStatuses,
    open,
    setOpen,
    selectedProject,
    setSelectedProject,
    openedProjectTab,
    setOpenedProjectTab,
    lastSyncTime,
    quickSummaryFilter,
    setQuickSummaryFilter,
    expanded,
    setExpanded,
    openActiveBatchesModal,
    setOpenActiveBatchesModal,
    updateStatusModalOpen,
    setUpdateStatusModalOpen,
    selectedProjectForUpdate,
    setSelectedProjectForUpdate,
    actionSelectValues,
    setActionSelectValues,
    selectedBatchNumbers,
    setSelectedBatchNumbers,
    selectedUnitProcedures,
    setSelectedUnitProcedures,
    selectedCampaigns,
    setSelectedCampaigns,
    selectedStages,
    setSelectedStages,
    selectedBatches,
    setSelectedBatches,
    selectedOperations,
    setSelectedOperations,
    cmoMembersList,
    mfgPlantsList,
    deliverableTypesList,
    loading,
    apiError,
    // Functions
    loadDashboardData,
    setInitialCmoSite,
    setInitialCmoOrgId,
    setInitialDeliverableType,
    setQuickSummaryStatuses,
    setProjects,
    setLastSyncTime,
    setLoading,
    setFiltersLoading,
  } = useProductDevelopment();

  const handlers = useHandlers({
    cmoSite,
    cmoOrgId,
    deliverableType,
    searchCriteria,
    searchValue,
    currentPage,
    // UI-PD-Changes: pass current tile selection so a second click on the
    // same Quick Summary tile toggles the filter off.
    quickSummaryFilter,
    setCmoSite,
    setCmoOrgId,
    setCmo,
    setSite,
    setDeliverableType,
    setInitialCmoSite,
    setInitialCmoOrgId,
    setInitialDeliverableType,
    setQuickSummaryFilter,
    setSearchCriteria,
    setSearchValue,
    setCurrentPage,
    setProjects,
    setSelectedBatchNumbers,
    setSelectedUnitProcedures,
    setLastSyncTime,
    setQuickSummaryStatuses,
    setLoading,
    setFiltersLoading,
    setSnackbar,
    setFilterDialog,
    loadDashboardData,
  });

  const {
    handlePageChange,
    handleRowsPerPageChange,
    toggleExpand,
    handleOpenProjectTab,
    handleRefresh,
    totalPages,
    paginatedProjects,
  } = useProductDevelopmentHelpers({
    setSelectedCampaigns,
    setSelectedStages,
    setSelectedBatches,
    setSelectedOperations,
    setCurrentPage,
    setRowsPerPage,
    setOpen,
    setSelectedProject,
    setOpenedProjectTab,
    open,
    setLastSyncTime,
    projects,
    totalProjects,
    rowsPerPage,
    currentPage,
    loadDashboardData,
  });

  const selectedProjectData = selectedProject 
    ? paginatedProjects.find(p => p.id === selectedProject) 
    : null;

  // Track applied filters vs current selections
  const [appliedCampaign, setAppliedCampaign] = React.useState(null);
  const [appliedStage, setAppliedStage] = React.useState(null);
  const [appliedBatch, setAppliedBatch] = React.useState(null);
  const [appliedOperation, setAppliedOperation] = React.useState('All');

  // Auto-apply filters whenever a dropdown selection changes (Apply Filters
  // button removed) — CollapseProjectDetails re-fetches when the applied
  // batchId/operation props change, so the API fires immediately on change.
  React.useEffect(() => {
    if (selectedProject) {
      const currentCampaign = selectedCampaigns[selectedProject] || null;
      const currentStage = selectedStages[selectedProject] || null;
      const currentBatch = selectedBatches[selectedProject]?.batchId || null;
      const currentOperation = selectedOperations[selectedProject] || 'All';

      setAppliedCampaign(currentCampaign);
      setAppliedStage(currentStage);
      setAppliedBatch(currentBatch);
      setAppliedOperation(currentOperation);
    } else {
      setAppliedCampaign(null);
      setAppliedStage(null);
      setAppliedBatch(null);
      setAppliedOperation('All');
    }
  }, [selectedProject, selectedCampaigns, selectedStages, selectedBatches, selectedOperations]);

  // Resolve the selected campaign + batch into the IDs the tab APIs need, so
  // that changing the Campaign / Stage / Batch dropdowns refetches with the
  // correct payload. The selected campaign is authoritative (not inferred from
  // the batch); the batch must belong to that campaign, otherwise we fall back
  // to the campaign's first available batch (or null when it has none).
  const selectedTabContext = React.useMemo(() => {
    if (!selectedProjectData) return { collabrixCampaignID: null, batchId: null };
    const campaignsList = selectedProjectData.projectDetails?.campaigns || [];
    const selCampaignId = selectedCampaigns[selectedProject];
    const campaign =
      campaignsList.find((c) => c.campaignId === selCampaignId) || campaignsList[0] || null;

    // All batch IDs belonging to the selected campaign (across its stages).
    const campaignBatchIds = [];
    (campaign?.stages || []).forEach((s) =>
      (s.batches || []).forEach((b) => campaignBatchIds.push(b.batchId))
    );

    let batchId = selectedBatches[selectedProject]?.batchId ?? null;
    if (batchId == null || !campaignBatchIds.includes(batchId)) {
      batchId = campaignBatchIds[0] ?? null;
    }

    return {
      collabrixCampaignID:
        campaign?.collabrixCampaignID || campaign?.campaignID || campaign?.campaignId || null,
      batchId,
    };
  }, [selectedProjectData, selectedProject, selectedCampaigns, selectedBatches]);

  // State for messages/chat/view (shared with tabs)
  // viewType represents the CURRENT view user is seeing.
  // Default: Overall View (so chevron-expanded view starts as Overall).
  const [viewType, setViewType] = React.useState("overallView");
  const [showChatBox, setShowChatBox] = React.useState(false);
  const [showMessages, setShowMessages] = React.useState(false);

  // When project selection changes, just reset messages; keep current viewType
  React.useEffect(() => {
    if (selectedProject) {
      setShowMessages(false);
    } else {
      setShowMessages(false);
    }
  }, [selectedProject]);

  const handleViewTypeChange = (newViewType) => {
    setViewType(newViewType);
    // Don't close messages when switching view type
  };

  const handleMessagesClick = () => {
    setShowMessages(true);
    setViewType("overallView");
  };

  const handleBackToTabs = () => {
    setShowMessages(false);
  };

  const handleChatClick = () => {
    setShowChatBox(!showChatBox);
  };

  // Return to the main dashboard from the expanded (batch-level) view.
  // Clears the selected project and collapses the expanded row. (R21)
  const handleReturnToMainDashboard = () => {
    setSelectedProject(null);
    setOpen({});
    setShowMessages(false);
    setShowChatBox(false);
  };

  // ── Full-screen in-app views (no URL / not routed) ─────────────────────────
  // Project Analytics and Batch Genealogy open in place over the dashboard via
  // this state, driven through ProjectDevNavContext. The browser URL never
  // changes; closing a view simply returns to the dashboard (whose state is
  // preserved because ProductDevelopment stays mounted).
  const [overlay, setOverlay] = React.useState(null); // null | 'analytics' | 'genealogy' | 'gantt' | 'executive'
  const [genealogyProps, setGenealogyProps] = React.useState({
    batchContext: null,
    fromProjectAnalytics: false,
  });
  // A one-shot request to select a specific project tab by name once the
  // dashboard is showing again — used by Batch Genealogy's "Open Deviation Tab"
  // to land on the batch's Deviations tab. Consumed (cleared) by ProjectTabs.
  const [pendingProjectTab, setPendingProjectTab] = React.useState(null);

  const openProjectAnalytics = React.useCallback(() => setOverlay('analytics'), []);
  const openGanttChart = React.useCallback(() => setOverlay('gantt'), []);
  const openExecutiveSummary = React.useCallback(() => setOverlay('executive'), []);
  const openBatchGenealogy = React.useCallback((opts = {}) => {
    setGenealogyProps({
      batchContext: opts.batchContext || null,
      fromProjectAnalytics: Boolean(opts.fromProjectAnalytics),
    });
    setOverlay('genealogy');
  }, []);
  const closeOverlay = React.useCallback(() => setOverlay(null), []);

  // Point the dashboard's Campaign / CMO Stage / CMO Batch selection at a
  // specific batch (by batch number) within the selected project, so the
  // project tabs load that batch's data. Used when returning from Batch
  // Genealogy so the Deviations tab opens for the exact batch the report was
  // showing. Prefers a match inside the hinted campaign, then searches the
  // whole project. Returns true when the batch was found and selected.
  const selectBatchByNumber = React.useCallback((batchNumber, opts = {}) => {
    if (!selectedProject || batchNumber == null || batchNumber === '') return false;
    const campaigns = selectedProjectData?.projectDetails?.campaigns || [];
    const target = String(batchNumber);

    const campaignMatchesHint = (c) =>
      opts.campaignId != null &&
      (String(c.campaignId) === String(opts.campaignId) ||
        String(c.campaignID) === String(opts.campaignId) ||
        String(c.collabrixCampaignID) === String(opts.campaignId));

    const scan = (filterFn) => {
      for (const campaign of campaigns) {
        if (filterFn && !filterFn(campaign)) continue;
        for (const stage of campaign.stages || []) {
          for (const batch of stage.batches || []) {
            if (String(batch.batchNumber) === target) return { campaign, stage, batch };
          }
        }
      }
      return null;
    };

    // Prefer the report's campaign, then fall back to a project-wide search
    // (a lineage node can belong to a different campaign/stage than the report).
    const found = (opts.campaignId != null && scan(campaignMatchesHint)) || scan(null);
    if (!found) return false;

    const { campaign, stage, batch } = found;
    setSelectedCampaigns((prev) => ({ ...prev, [selectedProject]: campaign.campaignId }));
    setSelectedStages((prev) => ({ ...prev, [selectedProject]: stage.stageId }));
    setSelectedBatches((prev) => ({
      ...prev,
      [selectedProject]: { batchNumber: batch.batchNumber, batchId: batch.batchId },
    }));
    setSelectedOperations((prev) => ({ ...prev, [selectedProject]: 'All' }));
    return true;
  }, [
    selectedProject,
    selectedProjectData,
    setSelectedCampaigns,
    setSelectedStages,
    setSelectedBatches,
    setSelectedOperations,
  ]);

  // Close any full-screen view and request the named tab (e.g. "Deviations").
  // When a batchRef ({ batchNumber, campaignId }) is supplied, first point the
  // Campaign / CMO Stage / CMO Batch selection at that batch so the tab opens
  // for it; otherwise the currently selected batch is used. The dashboard's
  // state is preserved (ProductDevelopment stays mounted).
  const openProjectTab = React.useCallback((tabName, batchRef = null) => {
    if (batchRef && batchRef.batchNumber != null) {
      selectBatchByNumber(batchRef.batchNumber, { campaignId: batchRef.campaignId });
    }
    setPendingProjectTab(tabName || null);
    setOverlay(null);
  }, [selectBatchByNumber]);
  const clearPendingProjectTab = React.useCallback(() => setPendingProjectTab(null), []);

  const navValue = React.useMemo(
    () => ({
      openProjectAnalytics,
      openBatchGenealogy,
      openGanttChart,
      openExecutiveSummary,
      openProjectTab,
    }),
    [
      openProjectAnalytics,
      openBatchGenealogy,
      openGanttChart,
      openExecutiveSummary,
      openProjectTab,
    ]
  );

  // Expanded-view scroll region. Size it to fill from its own top down to the
  // bottom of the window so it (not the whole page) owns the vertical scroll —
  // this is what lets the filters/search scroll out of view while the sticky
  // tab strip stays visible. Measured live so it adapts to the current screen
  // height and the variable header height above it.
  const scrollRef = React.useRef(null);
  const [scrollMaxHeight, setScrollMaxHeight] = React.useState(null);
  // Height of the pinned project row ("main table"). The tab strip is offset by
  // this so it stacks directly beneath the project row when both are stuck.
  const projectTableRef = React.useRef(null);
  const [projectTableHeight, setProjectTableHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!selectedProject) {
      setScrollMaxHeight(null);
      setProjectTableHeight(0);
      return undefined;
    }
    const measure = () => {
      const el = scrollRef.current;
      if (el) {
        const top = el.getBoundingClientRect().top;
        const available = window.innerHeight - top - 16;
        setScrollMaxHeight(Math.max(360, Math.round(available)));
      }
      if (projectTableRef.current) {
        setProjectTableHeight(projectTableRef.current.offsetHeight || 0);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [selectedProject, selectedProjectData]);

  if (overlay === 'analytics') {
    return (
      <ProjectDevNavContext.Provider value={navValue}>
        <ProjectAnalytics
          onBack={closeOverlay}
          onOpenBatchGenealogy={() => openBatchGenealogy({ fromProjectAnalytics: true })}
        />
      </ProjectDevNavContext.Provider>
    );
  }

  if (overlay === 'gantt') {
    return (
      <ProjectDevNavContext.Provider value={navValue}>
        <GanttChart onBack={closeOverlay} />
      </ProjectDevNavContext.Provider>
    );
  }

  if (overlay === 'executive') {
    return (
      <ProjectDevNavContext.Provider value={navValue}>
        <ExecutiveSummary onBack={closeOverlay} />
      </ProjectDevNavContext.Provider>
    );
  }

  if (overlay === 'genealogy') {
    return (
      <ProjectDevNavContext.Provider value={navValue}>
        <BatchGenealogy
          batchContext={genealogyProps.batchContext}
          fromProjectAnalytics={genealogyProps.fromProjectAnalytics}
          onReturnToMainDashboard={closeOverlay}
          onClose={closeOverlay}
        />
      </ProjectDevNavContext.Provider>
    );
  }

  // Don't paint the dashboard for a role that's about to be redirected away.
  // Placed after every hook above so hook order is never conditional.
  if (screensAccessLoaded && !pdGranted) return null;

  return (
    <ProjectDevNavContext.Provider value={navValue}>
      <BackDropLoading />
      <Grid container justifyContent="center" style={{ overflowX: 'hidden', width: '100%' }}>
        <Grid item xs={12} style={{ width: '100%', overflowX: 'hidden' }}>
        {selectedProject && (
          <Box style={{ marginBottom: '0.5rem' }}>
            <Button
              onClick={handleReturnToMainDashboard}
              startIcon={<ChevronLeftIcon />}
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#000000',
                padding: '4px 4px',
              }}
            >
              Return to Main Dashboard
            </Button>
          </Box>
        )}

        <PdScrollWrapper
          selected={Boolean(selectedProject)}
          scrollRef={scrollRef}
          maxHeight={scrollMaxHeight}
        >
        <Grid
          container
          spacing={1}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'flex-end',
          }}
        >
          <FilterSection
            classes={classes}
            cmoSite={cmoSite}
            setCmoSite={setCmoSite}
            cmo={cmo}
            setCmo={setCmo}
            site={site}
            setSite={setSite}
            cmoOrgId={cmoOrgId}
            setCmoOrgId={setCmoOrgId}
            deliverableType={deliverableType}
            setDeliverableType={setDeliverableType}
            initialCmoSite={initialCmoSite}
            initialCmoOrgId={initialCmoOrgId}
            initialDeliverableType={initialDeliverableType}
            cmoMembersList={cmoMembersList}
            mfgPlantsList={mfgPlantsList}
            deliverableTypesList={deliverableTypesList}
            filtersLoading={filtersLoading}
            handleSaveFilters={handlers.handleSaveFilters}
            handleClearFilters={handlers.handleClearFilters}
          />

          <ActionButtons
            classes={classes}
            setOpenActiveBatchesModal={setOpenActiveBatchesModal}
          />
        </Grid>

        {!selectedProject && (
          <QuickSummarySection
            classes={classes}
            expanded={expanded}
            setExpanded={setExpanded}
            quickSummaryStatuses={quickSummaryStatuses}
            quickSummaryFilter={quickSummaryFilter}
            handleClearQuickSummary={handlers.handleClearQuickSummary}
            handleQuickStatus={handlers.handleQuickStatus}
            cmoSite={cmoSite}
            deliverableType={deliverableType}
            loading={loading || filtersLoading}
          />
        )}

        {selectedProject && (
          <Box style={{ marginTop: '0.5rem', marginBottom: '0px' }}>
            <DocumentNavigationTabs
              projectId={selectedProjectData?.projectId || null}
              campaignId={selectedCampaigns[selectedProject] || selectedProjectData?.projectDetails?.campaigns?.[0]?.campaignId || null}
              batchId={selectedBatches[selectedProject]?.batchId || null}
              operation={selectedOperations[selectedProject] || 'All'}
              onDownloadSuccess={(message) => {
                setSnackbar({
                  open: true,
                  message: message,
                  severity: 'success',
                });
              }}
              onDownloadError={(message) => {
                setSnackbar({
                  open: true,
                  message: message,
                  severity: 'error',
                });
              }}
            />
            <RefreshSection
              classes={classes}
              lastSyncTime={lastSyncTime}
              handleRefresh={handleRefresh}
            />
          </Box>
        )}

        {!selectedProject && (
          <SearchSection
            classes={classes}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleSearch={handlers.handleSearch}
            handleClearSearch={handlers.handleClearSearch}
            lastSyncTime={lastSyncTime}
            handleRefresh={handleRefresh}
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        )}

        {/* When a project is selected, the single project row ("main table") is
            pinned to the top of the scroll region — same behavior as the tab
            strip — so the batch context stays visible while the tab table
            scrolls. */}
        <Box
          ref={projectTableRef}
          style={
            selectedProject
              ? { position: 'sticky', top: 0, zIndex: 5, backgroundColor: '#ffffff', boxSizing: 'border-box' }
              : undefined
          }
        >
          <TableSection
            classes={classes}
            paginatedProjects={paginatedProjects}
            viewType={viewType}
            selectedProject={selectedProject}
            open={open}
            toggleExpand={toggleExpand}
            actionSelectValues={actionSelectValues}
            setActionSelectValues={setActionSelectValues}
            setSelectedProjectForUpdate={setSelectedProjectForUpdate}
            setUpdateStatusModalOpen={setUpdateStatusModalOpen}
            selectedBatchNumbers={selectedBatchNumbers}
            setSelectedBatchNumbers={setSelectedBatchNumbers}
            selectedUnitProcedures={selectedUnitProcedures}
            setSelectedUnitProcedures={setSelectedUnitProcedures}
            selectedCampaigns={selectedCampaigns}
            setSelectedCampaigns={setSelectedCampaigns}
            selectedStages={selectedStages}
            setSelectedStages={setSelectedStages}
            selectedBatches={selectedBatches}
            setSelectedBatches={setSelectedBatches}
            selectedOperations={selectedOperations}
            setSelectedOperations={setSelectedOperations}
            loading={loading || filtersLoading}
            apiError={apiError}
            mfgPlantsList={mfgPlantsList}
          />
        </Box>

        {selectedProjectData && (
          <Box style={{ marginTop: '0px' }}>
            <CollapseProjectDetails
              open={true}
              stickyTabs={true}
              scrollRegionHeight={scrollMaxHeight}
              stickyTopOffset={projectTableHeight}
              row={selectedProjectData}
              handleRefresh={handleRefresh}
              openedProjectTab={openedProjectTab[selectedProject]}
              handleOpenProjectTab={(val) => handleOpenProjectTab(selectedProject, val)}
              projectColumnConfig={null}
              batchId={selectedTabContext.batchId}
              collabrixCampaignID={selectedTabContext.collabrixCampaignID}
              projectId={selectedProjectData?.projectId || null}
              campaignId={selectedCampaigns[selectedProject] || selectedProjectData?.projectDetails?.campaigns?.[0]?.campaignId || null}
              batchNumber={selectedBatches[selectedProject]?.batchNumber || null}
              operation={appliedOperation !== 'All' ? appliedOperation : (selectedOperations[selectedProject] || 'All')}
              viewType={viewType}
              showChatBox={showChatBox}
              showMessages={showMessages}
              initialTabName={pendingProjectTab}
              onInitialTabConsumed={clearPendingProjectTab}
              onViewTypeChange={handleViewTypeChange}
              onMessagesClick={handleMessagesClick}
              onChatClick={handleChatClick}
              onBackToTabs={handleBackToTabs}
            />
          </Box>
        )}

        {!selectedProject && (
          <PaginationSection
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
          />
        )}
        </PdScrollWrapper>
      </Grid>
      <ModalsSection
        updateStatusModalOpen={updateStatusModalOpen}
        setUpdateStatusModalOpen={setUpdateStatusModalOpen}
        selectedProjectForUpdate={selectedProjectForUpdate}
        setSelectedProjectForUpdate={setSelectedProjectForUpdate}
        openActiveBatchesModal={openActiveBatchesModal}
        setOpenActiveBatchesModal={setOpenActiveBatchesModal}
        cmoSite={cmoSite}
        deliverableType={deliverableType}
        onRefresh={loadDashboardData}
        setSnackbar={setSnackbar}
      />
      <SnackbarSection
        snackbar={snackbar}
        handleCloseSnackbar={handlers.handleCloseSnackbar}
      />
      <FilterDialogSection
        filterDialog={filterDialog}
        handleCloseFilterDialog={handlers.handleCloseFilterDialog}
      />
      </Grid>
    </ProjectDevNavContext.Provider>
  );
};

export default ProductDevelopment;
